import { useState } from 'react';

import type { OrganisationLocation, OrganisationTeam, Tenant } from '../domain-types';
import { organisationDisplayName } from '../utils/organisation-display';
import { OrganisationStructurePanel } from './tenant-panel';

export function OrganisationPage({
  tenant,
  editable,
  busy,
  onSaveStructure,
}: {
  tenant: Tenant | null;
  editable: boolean;
  busy: boolean;
  onSaveStructure: (structure: {
    locations: OrganisationLocation[];
    teams: OrganisationTeam[];
  }) => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<'overview' | 'structure'>('overview');

  if (!tenant) {
    return (
      <section className="organisation-page" aria-labelledby="organisation-page-title">
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Organisation</p>
            <h2 id="organisation-page-title">Organisation</h2>
          </div>
          <span>View only</span>
        </div>
        <p className="empty-state">No organisation is currently selected.</p>
      </section>
    );
  }

  return (
    <div className="organisation-page-layout">
      <section className="organisation-page" aria-labelledby="organisation-page-title">
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Organisation</p>
            <h2 id="organisation-page-title">{organisationDisplayName(tenant.name)}</h2>
          </div>
          <span>{editable ? 'Organisation Administrator' : 'View only'}</span>
        </div>
        <div aria-label="Organisation sections" className="organisation-page-tabs" role="tablist">
          <button
            aria-selected={activeTab === 'overview'}
            className={activeTab === 'overview' ? 'active' : undefined}
            role="tab"
            type="button"
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            aria-selected={activeTab === 'structure'}
            className={activeTab === 'structure' ? 'active' : undefined}
            role="tab"
            type="button"
            onClick={() => setActiveTab('structure')}
          >
            Structure
          </button>
        </div>
        {activeTab === 'overview' ? (
          <dl className="organisation-page-summary" role="tabpanel">
            <div>
              <dt>Organisation name</dt>
              <dd>{organisationDisplayName(tenant.name)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{tenant.status.replaceAll('_', ' ')}</dd>
            </div>
            <div>
              <dt>Organisation reference</dt>
              <dd>{tenant.slug}</dd>
            </div>
          </dl>
        ) : null}
      </section>
      <OrganisationStructurePanel
        busy={busy}
        editable={editable}
        locations={tenant.locations ?? []}
        onSave={onSaveStructure}
        teams={tenant.teams ?? []}
        visible={activeTab === 'structure'}
      />
    </div>
  );
}
